import React, { useState, useEffect } from "react";
import { Search, Settings, Menu, X, Sun, Moon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import FeaturesSection from "./FeaturesSection";
import HowItWorksSection from "./HowItWorksSection";
import TrackTimeSection from "./TrackTimeSection";
import StatsTeamSection from "./StatsTeamSection";
import ContactSection from "./ContactSection";
import Footer from "./Footer";
import Image1 from "./../Assets/Image1.png";
import Image2 from "./../Assets/Design 5 Phone.png";
import AppStore from "./../Assets/App Store Icon.png";
import PlayStore from "./../Assets/Playstore Icon.png";
import Logo from "./../Assets/Logo Web.png";
import MainLogo from "./../Assets/Homeegram Logo Set App.png";


const NaxosLandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 font-['Poppins'] ${
        isDarkMode ? "bg-black" : "bg-white"
      }`}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {/* Navigation Bar */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`transition-all duration-300 ${
          isScrolled
            ? isDarkMode
              ? "bg-black/95 backdrop-blur-md shadow-lg"
              : "bg-white/95 backdrop-blur-md shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <motion.div
                className={`flex items-center text-2xl font-semibold transition-colors ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                  {/* <img style={{ width: 40 }} src={Logo} className="mr-2" />
                  Homeegram */}
              </motion.div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <motion.a
                href="#features"
                className={`hover:text-[#FEF200] transition-colors ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Features
              </motion.a>
              <motion.a
                href="#vision"
                className={`hover:text-[#FEF200] transition-colors ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Vision
              </motion.a>
              <motion.a
                href="#contact"
                className={`hover:text-[#FEF200] transition-colors ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact
              </motion.a>

              {/* Theme Toggle */}
              <motion.button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${
                  isDarkMode
                    ? "bg-gray-800 text-[#FEF200] hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                whileHover={{ scale: 1.1, rotate: 15 }}
                whileTap={{ scale: 0.9 }}
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-4">
              <motion.button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${
                  isDarkMode
                    ? "bg-gray-800 text-[#FEF200] hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                whileHover={{ scale: 1.1, rotate: 15 }}
                whileTap={{ scale: 0.9 }}
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </motion.button>
              <motion.button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-md ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
                whileTap={{ scale: 0.9 }}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </motion.button>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className={`md:hidden ${
                  isDarkMode ? "bg-black" : "bg-white"
                } border-t ${
                  isDarkMode ? "border-gray-800" : "border-gray-200"
                }`}
              >
                <div className="px-2 pt-2 pb-3 space-y-1">
                  <motion.a
                    href="#features"
                    className={`block px-3 py-2 rounded-md hover:bg-gray-100 ${
                      isDarkMode
                        ? "text-gray-300 hover:bg-gray-800"
                        : "text-gray-700"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    Features
                  </motion.a>
                  <motion.a
                    href="#vision"
                    className={`block px-3 py-2 rounded-md hover:bg-gray-100 ${
                      isDarkMode
                        ? "text-gray-300 hover:bg-gray-800"
                        : "text-gray-700"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    Vision
                  </motion.a>
                  <motion.a
                    href="#contact"
                    className={`block px-3 py-2 rounded-md hover:bg-gray-100 ${
                      isDarkMode
                        ? "text-gray-300 hover:bg-gray-800"
                        : "text-gray-700"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Contact
                  </motion.a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section
        className={`relative overflow-hidden min-h-screen flex items-center  ${
          isDarkMode ? "bg-black" : "bg-white"
        }`}
      >
        {/* Hero Content */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 pt-20">
          <div className="max-w-6xl mx-auto text-center">
            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={`text-5xl sm:text-6xl lg:text-7xl font-semibold mb-8 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
            <img src={MainLogo} alt="Homeegram Logo" className="mx-auto w-xl" />
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={`text-lg sm:text-xl lg:text-2xl mb-12 max-w-3xl mx-auto ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              India's First Unified Construction Social Networking Platform.
            </motion.p>

            {/* App Store Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16"
            >
              <motion.a
                href="https://play.google.com/store/apps/details?id=com.homeeera.homeegram"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <img
                  style={{ width: 200 }}
                  src={PlayStore}
                  alt="Download from Play Store"
                  className="cursor-pointer"
                />
              </motion.a>

              <motion.a
                href="https://apps.apple.com/in/app/homeegram/id6751441806"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <img
                  style={{ width: 200 }}
                  src={AppStore}
                  alt="Download from App Store"
                  className="cursor-pointer"
                />
              </motion.a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex justify-center mb-16"
            >
              <motion.img
                src={Image2}
                alt="Homeegram App Interface"
                className="max-w-lg w-full"
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                whileHover={{ scale: 1.02 }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dream Connect Build Statement */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
        className={`text-center mb-30 px-4 ${
          isDarkMode ? "text-gray-200" : "text-gray-800"
        }`}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4">
          Dream. Connect. Build.
        </h2>
        <p className="text-lg max-w-4xl mx-auto leading-relaxed">
          Your vision deserves the right people. <br/>Homeegram enables seamless
          collaboration across the construction ecosystem,<br/>So you can dream big,
          connect smart, & build better.
        </p>
      </motion.div>

      {/* Sections */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      >
        <FeaturesSection isDarkMode={isDarkMode} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      >
        <TrackTimeSection isDarkMode={isDarkMode} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      >
        <StatsTeamSection isDarkMode={isDarkMode} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      >
        <ContactSection isDarkMode={isDarkMode} />
      </motion.div>

      {/* Additional Image */}
      <motion.img
        src={Image1}
        alt="Homeegram Community"
        className=""
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      />

      <Footer isDarkMode={isDarkMode} />

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {isScrolled && (
          <motion.div
            className="fixed bottom-8 right-8 z-50"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            <motion.button
              onClick={scrollToTop}
              className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700 text-white"
                  : "bg-black hover:bg-gray-800 text-white"
              }`}
              whileHover={{ scale: 1.1, y: -3 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }

        .h-120 {
          height: 30rem;
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom hover effects */
        .group:hover .group-hover\\:scale-110 {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  );
};

export default NaxosLandingPage;
